"use client";

import Header from '@/components/Header';
import ActionCard from '@/components/ActionCard';
import { mockActions, mockStats } from '@/lib/mockData';
import { Action } from '@/lib/types';
import styles from './page.module.css';
import { TrendingUp, TrendingDown, DollarSign, Activity, Percent } from 'lucide-react';
import clsx from 'clsx';
import { useState } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';

export default function Home() {
  const { t } = useLanguage();
  const [actions, setActions] = useState(mockActions);
  const [filter, setFilter] = useState<'all' | 'pricing' | 'restriction'>('all');

  const handleApprove = (id: string) => {
    // Optimistic update: remove form list or mark as done
    setActions(curr => curr.map(a =>
      a.id === id ? { ...a, status: 'executed' as const } : a
    ));
    // In real app: POST /api/actions/{id}/approve
  };

  const handleReject = (id: string) => {
    setActions(curr => curr.filter(a => a.id !== id));
  };

  const filteredActions = actions.filter(a => {
    if (filter === 'all') return true;
    // Mock logic for filtering based on title/desc keywords
    if (filter === 'pricing') return a.title.includes('Rate');
    if (filter === 'restriction') return a.title.includes('Stay') || a.title.includes('Restriction');
    return true;
  });

  return (
    <div className={styles.container}>
      <Header title={t('nav.today')} />

      <main className={styles.dashboard}>
        {/* KPI Cards (Unchanged) */}
        <section className={styles.statsGrid}>
          <div className={clsx('glass-card', styles.statCard)}>
            <div className={styles.statLabel}>{t('stat.occupancy')}</div>
            <div className={styles.statValue}>{mockStats.occupancy}%</div>
            <div className={clsx(styles.statTrend, styles.trendUp)}>
              <TrendingUp size={14} /> +2% {t('stat.trend.vsLastWeek')}
            </div>
          </div>

          <div className={clsx('glass-card', styles.statCard)}>
            <div className={styles.statLabel}>{t('stat.adr')}</div>
            <div className={styles.statValue}>€{mockStats.adr}</div>
            <div className={clsx(styles.statTrend, styles.trendUp)}>
              <TrendingUp size={14} /> +€12 {t('stat.trend.vsLastWeek')}
            </div>
          </div>

          <div className={clsx('glass-card', styles.statCard)}>
            <div className={styles.statLabel}>{t('stat.revpar')}</div>
            <div className={styles.statValue}>€{mockStats.revpar}</div>
            <div className={clsx(styles.statTrend, styles.trendDown)}>
              <TrendingDown size={14} /> -€3 {t('stat.trend.vsLastWeek')}
            </div>
          </div>

          <div className={clsx('glass-card', styles.statCard)}>
            <div className={styles.statLabel}>{t('stat.revenue')}</div>
            <div className={styles.statValue}>€{mockStats.revenue}</div>
            <div className={styles.statTrend}>
              <Activity size={14} /> {t('stat.trend.onPace')}
            </div>
          </div>
        </section>

        {/* Action Feed */}
        <section>
          <div className={styles.feedHeader}>
            <h2 className={styles.sectionTitle}>
              {t('dashboard.actions.title')}
              <span className={styles.actionCount}>{filteredActions.length}</span>
            </h2>

            {/* Filters */}
            <div className={styles.filters}>
              <button
                className={clsx(styles.filterChip, filter === 'all' && styles.filterActive)}
                onClick={() => setFilter('all')}
              >
                {t('filter.all')}
              </button>
              <button
                className={clsx(styles.filterChip, filter === 'pricing' && styles.filterActive)}
                onClick={() => setFilter('pricing')}
              >
                {t('filter.pricing')}
              </button>
              <button
                className={clsx(styles.filterChip, filter === 'restriction' && styles.filterActive)}
                onClick={() => setFilter('restriction')}
              >
                {t('filter.restriction')}
              </button>
            </div>
          </div>

          <div className={styles.actionsList}>
            {filteredActions.map(action => (
              <ActionCard
                key={action.id}
                action={action}
                onApprove={handleApprove}
                onReject={handleReject}
              />
            ))}
            {filteredActions.length === 0 && (
              <div className={styles.emptyState}>{t('dashboard.actions.empty')}</div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
